import { memo, type ReactElement } from 'react';
import { Button, IconButton } from '@chakra-ui/react';
import { useSelector } from 'react-redux';

import { getPlanPageThemeColors } from 'redux/ThemeSlice';

import {
  PLAN_TABLE_TIME_TOGGLE_RADIUS,
  PLAN_TABLE_TIME_TOGGLE_SIZE,
  PLAN_TEXT,
} from '../planPageRevampUi';

export const PLAN_TOGGLE_BUTTON_SIZE = '32px';
export const PLAN_TOGGLE_BUTTON_RADIUS = '10px';

export type PlanToggleButtonVariant = 'default' | 'tableTime';

interface Properties {
  isActive: boolean;
  onClick: () => void;
  label?: string;
  icon?: ReactElement;
  ariaLabel?: string;
  variant?: PlanToggleButtonVariant;
}

const getToggleButtonStyles = (
  isActive: boolean,
  primary: string,
  activeBg: string,
  border: string,
  surface: string,
  variant: PlanToggleButtonVariant,
) => {
  if (variant === 'tableTime') {
    return {
      h: PLAN_TABLE_TIME_TOGGLE_SIZE,
      w: PLAN_TABLE_TIME_TOGGLE_SIZE,
      minW: PLAN_TABLE_TIME_TOGGLE_SIZE,
      borderRadius: PLAN_TABLE_TIME_TOGGLE_RADIUS,
      bg: surface,
      border: '1px solid',
      borderColor: isActive ? primary : '#E2D9F3',
      color: isActive ? primary : '#708090',
      fontWeight: isActive ? '600' : '500',
      boxShadow: 'none',
      transition: 'border-color 0.15s ease, color 0.15s ease',
      _hover: {
        bg: surface,
        borderColor: primary,
        color: primary,
      },
      _active: { bg: surface },
      _focus: { outline: 'none', boxShadow: 'none' },
      _focusVisible: {
        outline: 'none',
        boxShadow: `0 0 0 2px ${surface}, 0 0 0 3px ${primary}`,
      },
    };
  }

  return {
    h: PLAN_TOGGLE_BUTTON_SIZE,
    w: PLAN_TOGGLE_BUTTON_SIZE,
    minW: PLAN_TOGGLE_BUTTON_SIZE,
    borderRadius: PLAN_TOGGLE_BUTTON_RADIUS,
    bg: activeBg,
    border: isActive ? '2px solid' : '1px solid',
    borderColor: isActive ? primary : border,
    color: primary,
    boxShadow: 'none',
    transition: 'border-color 0.15s ease, background 0.15s ease',
    _hover: {
      bg: activeBg,
      borderRadius: PLAN_TOGGLE_BUTTON_RADIUS,
      borderColor: primary,
      color: primary,
    },
    _active: {
      bg: activeBg,
      borderRadius: PLAN_TOGGLE_BUTTON_RADIUS,
    },
    _focus: {
      outline: 'none',
      boxShadow: 'none',
      borderRadius: PLAN_TOGGLE_BUTTON_RADIUS,
    },
    _focusVisible: {
      outline: 'none',
      boxShadow: `0 0 0 2px ${activeBg}, 0 0 0 3px ${primary}`,
      borderRadius: PLAN_TOGGLE_BUTTON_RADIUS,
    },
  };
};

const PlanToggleSquareButton = ({
  isActive,
  onClick,
  label,
  icon,
  ariaLabel,
  variant = 'default',
}: Properties) => {
  const themeColors = useSelector(getPlanPageThemeColors);
  const toggleStyles = getToggleButtonStyles(
    isActive,
    themeColors.primary,
    themeColors.activeBg,
    themeColors.border,
    themeColors.surface,
    variant,
  );

  if (icon) {
    return (
      <IconButton
        aria-label={ariaLabel ?? label ?? 'Toggle'}
        icon={icon}
        size='sm'
        variant='unstyled'
        display='inline-flex'
        alignItems='center'
        justifyContent='center'
        onClick={onClick}
        {...toggleStyles}
      />
    );
  }

  return (
    <Button
      size='sm'
      variant='unstyled'
      display='inline-flex'
      alignItems='center'
      justifyContent='center'
      px={0}
      {...(variant === 'tableTime' ? PLAN_TEXT.labelSmMedium : PLAN_TEXT.tableHeader)}
      lineHeight='1'
      onClick={onClick}
      {...toggleStyles}
    >
      {label}
    </Button>
  );
};

export default memo(PlanToggleSquareButton);
